import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo } from 'react'
import { makeTextFieldIcon, TextFieldPlaceholder } from 'shared/ui/TextField'
import { logger, sameWidth } from 'shared/lib'
import { Box, Dropdown, Error, Label, Typography } from 'shared/ui'
import { ChipWithAvatar } from 'entities/Phone/ui/AssignNumberCommon/ui/ChipWithAvatar/ChipWithAvatar'
import { MultiSelectDropdownContent } from 'entities/Phone/ui/AssignNumberCommon/ui/NewInboxContent/MultiSelectDropdownContent'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { userToDropdownItem } from 'entities/Users/lib/userToDropdownItem'
import { type UsersMultiDropdownStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/Ring/ui/RingActionEditContent/UsersMultiDropdown/store/UsersMultiDropdownStore'
import { RenderItemsContent } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/Ring/ui/RingActionEditContent/UsersMultiDropdown/ui/RenderItemsContent/RenderItemsContent'
import styles from './styles.module.scss'

export const UsersMultiDropdown = observer(({ store }: { store: UsersMultiDropdownStore }) => {
  const { usersStore } = useEntitiesContext()

  const {
    setSelectedMembers,
    deleteSelectedMembers,
    selectedMembers,
    error,
    setError,
    initUsersIds,
    setInitMembers,
  } = store

  useEffect(() => {
    if (initUsersIds) {
      usersStore.initMembersPromise.then(async () => {
        try {
          const items = await usersStore.getItemsByIds(initUsersIds)
          setInitMembers(items.map(userToDropdownItem))
        } catch (e) {
          logger.error(e)
        }
      })
    }
  }, [initUsersIds])

  const selectedMembersItems = useMemo(() => {
    return selectedMembers.map((item) => ({
      id: item.id,
      label: item.label,
      value: item.value,
      data: {
        isAway: undefined,
        avatarInfo: item.avatarProps?.info,
        email: item.data?.email,
      },
    }))
  }, [selectedMembers])

  return (
    <Box display={'flex'} flexDirection={'column'} gap={0.5}>
      <Label
        label={{
          text: 'Users',
        }}
      />
      <Dropdown
        width={'100%'}
        triggerComponent={() => (
          <TextFieldPlaceholder
            size={'medium'}
            variant={'stroke'}
            className={styles.multiSelect}
            placeholderClassName={styles.placeholder}
            onClick={() => setError('')}
            error={error}
            placeholder={
              <>
                {selectedMembersItems.length ? (
                  <RenderItemsContent
                    items={selectedMembersItems}
                    onDelete={deleteSelectedMembers}
                  />
                ) : (
                  <Typography
                    ariaLabel={'select'}
                    variant={'text-input-field-r'}
                    color={'var(--content-neutral-primary)'}
                    className={styles.empty}
                  >
                    Select users
                  </Typography>
                )}
              </>
            }
            rightActions={[
              makeTextFieldIcon({
                icon: 'chevronDown',
              }),
            ]}
          />
        )}
        customComponent={() => (
          <MultiSelectDropdownContent
            ChipComponent={ChipWithAvatar}
            items={usersStore.membersDropdownItems}
            selectedItems={selectedMembersItems}
            onSelect={setSelectedMembers}
            onDelete={deleteSelectedMembers}
          />
        )}
        PopperProps={{
          modifiers: [
            sameWidth(),
            {
              name: 'offset',
              options: {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                offset: ({ reference }) => {
                  return [0, -reference.height - 8]
                },
              },
            },
          ],
        }}
      />
      {error && <Error type={'text'} error={error} />}
    </Box>
  )
})
